"use client";

import * as React from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils";

const tabsVariants = cva("", {
  variants: {
    variant: {
      default: "",
      underlined: "al-w-full",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

const tabsListVariants = cva(
  "al-inline-flex al-items-center al-justify-center al-text-muted-foreground",
  {
    variants: {
      variant: {
        default: "al-h-10 al-rounded-md al-bg-muted al-p-1",
        underlined:
          "!al-justify-start al-w-full al-bg-transparent al-border-b al-rounded-none !al-p-0",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const tabsTriggerVariants = cva(
  "al-inline-flex al-items-center al-justify-center al-whitespace-nowrap al-text-sm al-font-medium al-ring-offset-background al-transition-all focus-visible:al-outline-none focus-visible:al-ring-2 focus-visible:al-ring-ring focus-visible:al-ring-offset-2 disabled:al-pointer-events-none disabled:al-opacity-50",
  {
    variants: {
      variant: {
        default:
          "al-rounded-sm al-px-3 al-py-1.5 data-[state=active]:al-bg-background data-[state=active]:al-text-foreground data-[state=active]:al-shadow-sm",
        underlined:
          "!al-rounded-none al-bg-transparent al-h-full data-[state=active]:shadow-none al-border-b-2 al-border-transparent data-[state=active]:al-border-primary al-px-3 al-py-1.5",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const TabsContext = React.createContext<{ variant?: "default" | "underlined" | null | undefined }>({
  variant: "default",
});

export interface TabsProps
  extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root>,
    VariantProps<typeof tabsVariants> {}

const Tabs = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Root>, TabsProps>(
  ({ className, variant, ...props }, ref) => (
    <TabsContext.Provider value={{ variant }}>
      <TabsPrimitive.Root
        ref={ref}
        className={cn(tabsVariants({ variant }), className)}
        {...props}
      />
    </TabsContext.Provider>
  )
);
Tabs.displayName = TabsPrimitive.Root.displayName;

const TabsList = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.List>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.List
      ref={ref}
      className={cn(tabsListVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
  const { variant } = React.useContext(TabsContext);
  return (
    <TabsPrimitive.Trigger
      ref={ref}
      className={cn(tabsTriggerVariants({ variant }), className)}
      {...props}
    />
  );
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
  React.ElementRef<typeof TabsPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      "al-mt-2 al-ring-offset-background focus-visible:al-outline-none focus-visible:al-ring-2 focus-visible:al-ring-ring focus-visible:al-ring-offset-2",
      className
    )}
    {...props}
  />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent, tabsVariants };
